import { Realtime } from "ably";
import {
  computed,
  inject,
  ref,
  watchEffect,
  type App,
  type InjectionKey,
} from "vue";
import type Ably from "ably";
import { useAppStore } from "@/stores/app.store";
import { getSharedHeaders } from "@/utils/headers";
import { APP_CONNECTION_ID_HEADER } from "@f3/shared/identity";
import { isSimulfestSession } from "@/utils/simulfest";
import { waitFor, calculateExponentialBackoff } from "@/utils/misc";

export const ablySdkLogPrefix = "[Ably] ";

const ABLY_SDK_MAX_AUTH_ATTEMPTS = 10;

let __globalAblySdk: Realtime | null = null;
const apiURL = import.meta.env.VITE_API_URL;

function ensureOneAblySdk(defaultClientId: string) {
  const isSimulfest = isSimulfestSession();

  // Only initialize the Ably SDK once
  if (__globalAblySdk === null) {
    // Initialize Ably SDK with token from server (ours)
    __globalAblySdk = new Realtime({
      logHandler: (msg, level) => {
        console.log(ablySdkLogPrefix + `log - ${level} ${msg}`);
      },
      logLevel: isSimulfest ? 4 : 0, // Add debugging logs for Simulfest ONLY
      transportParams: {
        // Roughly similar presence settings to Chime SDK
        remainPresentFor: 5 * 1000,
        heartbeatInterval: 5000,
      },
      authCallback: async (params, callback) => {
        try {
          // Send our connection id so the server scopes OTP-status read to
          // this connection's own channel only (no broad api-events:*).
          const connectionId =
            getSharedHeaders()[APP_CONNECTION_ID_HEADER] ?? "";
          const query = new URLSearchParams({
            clientId: params.clientId ?? defaultClientId,
            ...(connectionId ? { connectionId } : {}),
          }).toString();
          const response = await fetch(`${apiURL}/sockets?${query}`);
          const tokenRequest = await response.json();
          callback(null, tokenRequest);
        } catch (error) {
          // @ts-expect-error -- caught value is unknown; Ably's authCallback error param is typed ErrorInfo | string
          callback(error, null);
        }
      },
      autoConnect: false,
      queueMessages: false,
      disconnectedRetryTimeout: 2000,
      suspendedRetryTimeout: 4000,
    });
    __globalAblySdk.connection
      .once("connected")
      .then(() => console.log(ablySdkLogPrefix + "Connected to Ably"));
  }

  return __globalAblySdk;
}

export const ablySdkInjectionKey = Symbol("AblySdk") as InjectionKey<
  ReturnType<typeof provideAblySdk>
>;

export function provideAblySdk(app: App) {
  if (__globalAblySdk !== null) {
    throw new Error(
      "useProvideAblySdk must be called only once, but it was called multiple times. Please ensure it is called only once in the root of the application, or that previously created instances are properly disposed of. Otherwise it may lead to unexpected behavior with Tokens from the Ably SDK.",
    );
  }

  const appStore = useAppStore();
  const ablySdk = ensureOneAblySdk(appStore.connectionId);

  // @ts-expect-error -- ff_debug is an ad-hoc debug hook not declared on the Window type
  window.ff_debug = (connect: boolean) => {
    if (connect) {
      ablySdk.connect();
    } else {
      ablySdk.close();
    }
  };

  const isConnected = ref(
    ablySdk.connection.state === "connected" ||
      ablySdk.connection.state === "connecting",
  );
  const connectionState = ref<Ably.ConnectionState>(ablySdk.connection.state);
  const connectionErrorReason = ref<Ably.ErrorInfo | null>(
    ablySdk.connection.errorReason ?? null,
  );
  const authAttempts = ref(0);
  const isAuthRetryingMaxAttempts = computed(
    () => authAttempts.value >= ABLY_SDK_MAX_AUTH_ATTEMPTS,
  );

  const isReconnecting = computed(
    () =>
      !isConnected.value &&
      authAttempts.value > 0 &&
      authAttempts.value < ABLY_SDK_MAX_AUTH_ATTEMPTS,
  );

  const connectionId = computed(() => ablySdk.connection.id ?? null);

  function authorizeAblySdkWithClientId(clientId: string) {
    return new Promise<boolean>((resolve) => {
      // Disconnect the SDK before re-authorizing
      isConnected.value = false;

      ablySdk.auth
        .authorize({ clientId: clientId })
        .then(() => {
          console.log(ablySdkLogPrefix + "Ably SDK authorized");
          isConnected.value = true;
          resolve(true);
        })
        .catch((error) => {
          console.error(
            ablySdkLogPrefix + "Ably SDK authorization failed",
            error,
          );
          isConnected.value = false;

          resolve(false);
        })
        .then(() => {
          authAttempts.value++;
        });
    });
  }

  watchEffect((onCleanup) => {
    const updateConnectionState = (change?: Ably.ConnectionStateChange) => {
      const state = change?.current ?? ablySdk.connection.state;
      connectionState.value = state;
      connectionErrorReason.value =
        change?.reason ?? ablySdk.connection.errorReason ?? null;
      isConnected.value = state === "connected" || state === "connecting";

      if (state === "connected") {
        console.log(ablySdkLogPrefix + "Ably SDK connected");
        authAttempts.value = 0;
      } else if (state === "disconnected") {
        console.log(ablySdkLogPrefix + "Ably SDK disconnect");
      } else if (state === "closed") {
        console.log(ablySdkLogPrefix + "Ably SDK closed");
      }
    };

    updateConnectionState();
    ablySdk.connection.on(updateConnectionState);

    onCleanup(() => {
      ablySdk.connection.off(updateConnectionState);
    });
  });

  watchEffect(async (onCleanup) => {
    const sessionId = appStore.connectionId;
    const isSimulfest = isSimulfestSession();

    // Skip focus check for Simulfest (automated environment may not have focus)
    if (isConnected.value || (!isSimulfest && !appStore.appHasFocus)) return;

    if (authAttempts.value >= ABLY_SDK_MAX_AUTH_ATTEMPTS) {
      console.error(
        ablySdkLogPrefix +
          "Ably SDK authorization failed, max attempts reached",
      );
      return;
    }

    console.log(
      ablySdkLogPrefix +
        `Authorizing Ably SDK with client ID ${sessionId} (auth attempts: ${authAttempts.value})`,
    );

    const backoffDelayMs = calculateExponentialBackoff(authAttempts.value, {
      baseDelayMs: 1500,
      maxDelayMs: 30000,
    });

    console.log(
      ablySdkLogPrefix +
        `Waiting ${Math.round(backoffDelayMs)}ms before retry attempt`,
    );

    await waitFor(backoffDelayMs);
    authorizeAblySdkWithClientId(sessionId);

    onCleanup(() => {
      console.log(ablySdkLogPrefix + "Ably SDK authorization effect cleanup");
    });
  });

  watchEffect((onCleanup) => {
    onCleanup(() => {
      console.log(ablySdkLogPrefix + "Disconnecting Ably SDK");
      ablySdk.connection.off();
      ablySdk.close();
      __globalAblySdk = null;
    });
  });

  const definition = {
    isConnected,
    connectionState,
    connectionErrorReason,
    connectionId,
    sdk: ablySdk,
    isAuthRetryingMaxAttempts,
    isReconnecting,
    authAttempts,
  };

  app.provide(ablySdkInjectionKey, definition);
  return definition;
}

/**
 * Injects the Ably SDK instance along with metadata about the connection.
 *
 * Do not use directly in components, use higher-level composables instead.
 */
export function useAblySdk() {
  const ablySdk = inject(ablySdkInjectionKey);

  if (!ablySdk) {
    throw new Error(
      "useAblySdk must be used within a component that has been provided with the Ably SDK",
    );
  }

  return ablySdk;
}
